import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from '@tanstack/react-query';
import { useAuthedFetch } from '../useAuthedFetch';
import { TEMPLATE_BACKUPS_KEY, checklistKey } from './keys';

type RefreshTemplateArgs = { source: string };
type RefreshTemplateResult = {
  source: string;
  // Set when the live fetch failed and the local stored template was used.
  fallbackReason?: string;
  backupPartition: string | null;
  items: number;
};

type DeleteBackupArgs = { partition: string };
type DeleteBackupResult = { partition: string; items: number };

type RestoreBackupArgs = { partition: string };
type RestoreBackupResult = { backupPartition: string; items: number };

export const useRefreshDefaultTemplate = (): UseMutationResult<
  RefreshTemplateResult,
  Error,
  RefreshTemplateArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ source }) =>
      authedFetch(
        `/releases/default-template/refresh/${encodeURIComponent(source)}`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      // The `_default` partition's contents have changed — drop the cached
      // checklist so the next `Create new` flow re-fetches the fresh seed.
      // Each refresh also creates a new backup partition, so the list of
      // backups needs to refetch too.
      qc.removeQueries({ queryKey: checklistKey('_default') });
      qc.invalidateQueries({ queryKey: TEMPLATE_BACKUPS_KEY });
    },
  });
};

export const useRestoreDefaultTemplate = (): UseMutationResult<
  RestoreBackupResult,
  Error,
  RestoreBackupArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ partition }) =>
      authedFetch(
        `/releases/default-template/restore/${encodeURIComponent(partition)}`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      // Live `_default` was replaced — drop the cached checklist so the
      // next Create flow re-fetches. (No pre-restore snapshot is created
      // anymore; the source backup itself is the rollback path.)
      qc.removeQueries({ queryKey: checklistKey('_default') });
    },
  });
};

export const useDeleteDefaultTemplateBackup = (): UseMutationResult<
  DeleteBackupResult,
  Error,
  DeleteBackupArgs
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ partition }) =>
      authedFetch(
        `/releases/default-template/backups/${encodeURIComponent(partition)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEMPLATE_BACKUPS_KEY });
    },
  });
};
